/**
 * MotionsPage — IVT Hub "Sales Motion" list page (V14 motion-centric).
 *
 * Scope (DAT-8313 ListMotions + DAT-8315 Create dialog): the page CHROME +
 * the Motion list table backed by `ListMotions` + a working **Create New Motion**
 * dialog. Both backend handlers are implemented on mainline (ListMotions bfc1383,
 * CreateMotion 9480f1c).
 *
 * Data: a SINGLE ListMotions call (no pagination — curated low-cardinality set,
 * @length max 500), 4-state machine (loading / error / empty / data), cancelled-
 * guard, reloadKey for refresh-after-create. Columns:
 * Motion Name / Pipeline Status / Creation Time / Total ARR.
 *
 * Pipeline Status combines the pipeline total + tagged counts into one cell
 * ("Total 10, tagged 2"). The old separate Progress / # Pipeline Total /
 * # Pipeline Tagged columns were dropped per design feedback (2026-07-08).
 *
 * Figma source: PAGE "IVT" node 1920:12350 (Motion List) + 1956:31051 (Create
 * dialog). Per the 0630 design (drop view_type) the "By Industry / By Topic"
 * toggle is NOT built; the left filter bar is deferred until its fields are
 * confirmed against ListMotions (market has no schema carrier).
 *
 * Motion is a first-class entity now. The old asset-keyed prototype Motion pages
 * were removed in CR-285723348.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  type CollectionPreferencesProps,
  Header,
  Link,
  Pagination,
  SegmentedControl,
  SpaceBetween,
  Table,
  type TableProps,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  getIvtClient,
  DeleteMotionCommand,
  ListMotionsCommand,
  type MotionListItem,
} from '../../client/IvtHubClient';
import { useAuth } from '../../../context/AuthContext';
import { fetchAllowedIndustries, resolveIndustryQueryScope, isEmptyIndustryScope } from '../../client/permissionClient';
import MotionCreateModal from './components/MotionCreateModal';
import MotionDeleteModal from './components/MotionDeleteModal';
import MotionFilters, { type MotionFilterOption } from './components/MotionFilters';
import { useMotionFilterState } from './useMotionFilterState';
import MotionPipelineList from './components/MotionPipelineList';
import { reconcileVisibleContent } from '../../utils/tablePrefs';
import { denialHint, isDenied } from '../../utils/authorization';
import './MotionsPage.css';

type SubView = 'motions' | 'pipeline';

/** ISO timestamp → YYYY-MM-DD (TZ-safe: slice the date part, no toLocaleDateString
 *  which would shift the day by the local offset — the cronToZh TZ bug). */
function formatDate(iso?: string): string {
  if (!iso) return '—';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : iso;
}

/** Total ARR → "$1,250,000" (whole dollars). Empty when undefined. */
function formatArr(arr?: number): string {
  if (arr == null) return '—';
  return `$${Math.round(arr).toLocaleString('en-US')}`;
}

// ── Table preferences (aligned with the Pipeline List tab) ───────────────────
// Same shape/idiom as MotionPipelineList: page size + column show/hide via the
// preferences gear, drag-resizable columns, all persisted to localStorage. Tab1
// data is a single full ListMotions load, so pagination is pure client-side
// (useCollection) — no keyset. Each table owns its own prefs keys.
const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
  { value: 10, label: '10 条 / 页' },
  { value: 20, label: '20 条 / 页' },
  { value: 50, label: '50 条 / 页' },
];

const COLUMN_IDS = ['name', 'pipelineStatus', 'createdAt', 'totalArr'] as const;

const COLUMN_LABELS: Record<(typeof COLUMN_IDS)[number], string> = {
  name: 'Motion Name',
  pipelineStatus: 'Pipeline Status',
  createdAt: 'Creation Time',
  totalArr: 'Total ARR',
};

const COLUMN_MIN_WIDTH: Record<(typeof COLUMN_IDS)[number], number> = {
  name: 200,
  pipelineStatus: 180,
  createdAt: 130,
  totalArr: 120,
};

// 'name' is the row header (isRowHeader) — Cloudscape needs a row header, so it
// is not user-hideable (excluded from the options, always kept visible).
const ROW_HEADER_ID = 'name';
const HIDEABLE_COLUMN_IDS = COLUMN_IDS.filter((id) => id !== ROW_HEADER_ID);

const VISIBLE_CONTENT_OPTIONS: CollectionPreferencesProps.VisibleContentOptionsGroup[] = [
  { label: 'Motion 列', options: HIDEABLE_COLUMN_IDS.map((id) => ({ id, label: COLUMN_LABELS[id] })) },
];

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 20,
  visibleContent: [...HIDEABLE_COLUMN_IDS],
};

// v2: the column set changed (dropped Progress; merged # Pipeline Total + # Pipeline
// Tagged into one "Pipeline Status" column). Bump the keys so a returning user's
// stored visibleContent (which lists the removed ids and lacks pipelineStatus)
// doesn't hide the new column — start them fresh on the new default set.
const PREFS_STORAGE_KEY = 'ivt_motion_list_table_prefs_v2';
const WIDTHS_STORAGE_KEY = 'ivt_motion_list_col_widths_v2';

// One-time frozen baseline: hideable columns as of when the new-column-default-
// visible fix shipped. No column has been added to this table since, so the
// baseline is the full current set. NEVER edit this — later saves write their own
// knownColumns snapshot and reconcileVisibleContent handles new columns
// automatically. When you DO add a column, leave this alone (it stays the pre-add
// set) so returning users see the new column by default.
const LEGACY_KNOWN_COLUMNS: readonly string[] = [...HIDEABLE_COLUMN_IDS];

function loadPreferences(): CollectionPreferencesProps.Preferences {
  try {
    const stored = localStorage.getItem(PREFS_STORAGE_KEY);
    if (!stored) return DEFAULT_PREFERENCES;
    // Strip the persistence-only knownColumns snapshot so it doesn't leak into the
    // in-memory Cloudscape preferences object (rest = the real Preferences shape).
    const { knownColumns, ...rest } = JSON.parse(stored) as CollectionPreferencesProps.Preferences & {
      knownColumns?: string[];
    };
    const pageSize =
      typeof rest.pageSize === 'number' && Number.isInteger(rest.pageSize) && rest.pageSize > 0
        ? rest.pageSize
        : DEFAULT_PREFERENCES.pageSize;
    return {
      ...DEFAULT_PREFERENCES,
      ...rest,
      pageSize,
      visibleContent: reconcileVisibleContent({
        storedVisible: rest.visibleContent,
        storedKnown: knownColumns,
        hideableColumns: HIDEABLE_COLUMN_IDS,
        legacyKnownColumns: LEGACY_KNOWN_COLUMNS,
        defaultVisible: DEFAULT_PREFERENCES.visibleContent ?? HIDEABLE_COLUMN_IDS,
      }),
    };
  } catch {
    /* ignore — fall back to defaults */
  }
  return DEFAULT_PREFERENCES;
}

function savePreferences(prefs: CollectionPreferencesProps.Preferences) {
  try {
    // Snapshot the columns that exist NOW alongside the prefs, so a future load
    // can tell "user hid this" from "this didn't exist yet" (reconcileVisibleContent).
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify({ ...prefs, knownColumns: [...HIDEABLE_COLUMN_IDS] }));
  } catch {
    /* ignore */
  }
}

function loadColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(WIDTHS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, number>;
      return Object.fromEntries(
        Object.entries(parsed).filter(
          ([id, w]) => COLUMN_IDS.includes(id as (typeof COLUMN_IDS)[number]) && typeof w === 'number' && w > 0,
        ),
      );
    }
  } catch {
    /* ignore */
  }
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    /* ignore */
  }
}

export default function MotionsPage() {
  const { uid } = useAuth();
  const navigate = useNavigate();

  // Permission: allowed industries for the current user (via Permission Service).
  // fail-CLOSED 3-state machine (one atomic state object):
  //   - loading → the Motion list/pipeline fetches are GATED (spinner), so they
  //     never race ahead of permission with a wrong-scoped query.
  //   - error   → permission fetch FAILED (service down / non-200 / no uid). The
  //     list is NOT fetched; an error is shown. We do NOT fall open to "all
  //     industries" — a permission outage must never expose every industry.
  //   - ready   → `allowed` is the settled permitted set (MAY be empty = the user
  //     legitimately has no industry permission → an empty list, not an error).
  const [permission, setPermission] = useState<{
    status: 'loading' | 'error' | 'ready';
    allowed: string[];
  }>({ status: 'loading', allowed: [] });
  const allowedIndustries = permission.allowed;
  const permissionReady = permission.status === 'ready';
  const permissionError = permission.status === 'error';
  useEffect(() => {
    if (!uid) {
      // No user identity → can't scope → fail CLOSED (error, not open).
      setPermission({ status: 'error', allowed: [] });
      return;
    }
    let cancelled = false;
    setPermission({ status: 'loading', allowed: [] });
    (async () => {
      const result = await fetchAllowedIndustries(uid);
      if (cancelled) return;
      setPermission(
        result.ok ? { status: 'ready', allowed: result.industries } : { status: 'error', allowed: [] },
      );
    })();
    return () => { cancelled = true; };
  }, [uid]);
  // Which sub-tab is active, mirrored in the URL (?tab=motions|pipeline) so it
  // survives a round-trip to the Motion detail page: the detail breadcrumb links
  // back to /ivt/motions?tab=<origin>, and we init from that here. setSubTab
  // updates both the state and the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const subView: SubView = searchParams.get('tab') === 'pipeline' ? 'pipeline' : 'motions';
  const setSubView = (tab: SubView) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };
  const [creating, setCreating] = useState(false);

  const [motions, setMotions] = useState<MotionListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Multi-select → batch delete.
  const [selectedItems, setSelectedItems] = useState<MotionListItem[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Table preferences (page size + visible columns) + drag widths, persisted.
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>(loadPreferences);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadColumnWidths);

  // ── Filters ────────────────────────────────────────────────────────────────
  // Industry / Use Case / Topic / Asset are SERVER-SIDE (ListMotions
  // multi-value filters, OR semantics): changing them refetches. Motion is
  // CLIENT-SIDE — a multi-select of the loaded Motions' names; selecting some keeps
  // only those rows (this tab is one row = one Motion, so the backend offers no
  // Motion filter; pages-design). Empty = no filter.
  //
  // All persisted in sessionStorage (useMotionFilterState) so they survive a tab
  // switch AND a round-trip to the Motion detail page. The four DOMAIN filters use
  // SHARED keys (same selection on both tabs); Motion uses a List-tab-specific key
  // (independent from the Pipeline tab's Motion filter — different semantics).
  const [industries, setIndustries] = useMotionFilterState('industries');
  const [useCases, setUseCases] = useMotionFilterState('useCases');
  const [subTopics, setSubTopics] = useMotionFilterState('subTopics');
  const [assets, setAssets] = useMotionFilterState('assets');
  const [selectedMotions, setSelectedMotions] = useMotionFilterState('list.motions');

  // Motion dropdown options = the loaded Motions' names (value = motionId, unique;
  // label = name). Sorted by name.
  const motionNameOptions = useMemo<MotionFilterOption[]>(
    () =>
      (motions ?? [])
        .filter((m) => m.motionId)
        .map((m) => ({ value: m.motionId as string, label: m.name ?? (m.motionId as string) }))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', 'en')),
    [motions],
  );

  const filteredMotions = useMemo(() => {
    const all = motions ?? [];
    if (selectedMotions.length === 0) return all;
    const picked = new Set(selectedMotions.map((o) => o.value));
    return all.filter((m) => picked.has(m.motionId));
  }, [motions, selectedMotions]);

  // Server-filter values sent to ListMotions. subTopic option values are the
  // numeric subTopicId as a string (Multiselect values are strings) → parse back.
  const industryValues = useMemo(
    () => industries.map((o) => o.value).filter((v): v is string => !!v),
    [industries],
  );
  const useCaseValues = useMemo(
    () => useCases.map((o) => o.value).filter((v): v is string => !!v),
    [useCases],
  );
  const subTopicIdValues = useMemo(
    () => subTopics.map((o) => Number(o.value)).filter((n) => Number.isFinite(n)),
    [subTopics],
  );
  const assetIdValues = useMemo(
    () => assets.map((o) => o.value).filter((v): v is string => !!v),
    [assets],
  );

  const hasActiveFilter =
    selectedMotions.length > 0 ||
    industries.length > 0 ||
    useCases.length > 0 ||
    subTopics.length > 0 ||
    assets.length > 0;

  const clearAllFilters = () => {
    setSelectedMotions([]);
    setIndustries([]);
    setUseCases([]);
    setSubTopics([]);
    setAssets([]);
  };

  useEffect(() => {
    // Gate on permission (fail-CLOSED): only fetch the list once permission is
    // READY. While loading → spinner; on error → the list is never fetched (the
    // page renders a permission error instead, below). This guarantees a permission
    // outage can't fall open to an unrestricted (all-industries) query.
    if (!permissionReady) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Empty-scope short-circuit: a zero-permission user (empty resolved scope) must
    // NOT be sent as industries:[] — that serializes to no query param, an ambiguous
    // "no filter" request. Render an empty list and skip the round-trip. This is a
    // correctness/UX guard; the backend enforces industry permission server-side on
    // the LIST ops (P8), so it's no longer the security boundary. (isEmptyIndustryScope
    // docstring explains the serialization trap.)
    if (isEmptyIndustryScope(industryValues, allowedIndustries)) {
      setMotions([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        // industries: an EMPTY selection defaults to the ALLOWED set (fail-closed);
        // the empty-scope case is handled by the short-circuit above, so here the
        // resolved scope is always non-empty and serializes to a real filter.
        const out = await getIvtClient().send(
          new ListMotionsCommand({
            industries: resolveIndustryQueryScope(industryValues, allowedIndustries),
            useCases: useCaseValues.length ? useCaseValues : undefined,
            subTopicIds: subTopicIdValues.length ? subTopicIdValues : undefined,
            assetIds: assetIdValues.length ? assetIdValues : undefined,
          }),
        );
        if (cancelled) return;
        setMotions(out.motions ?? []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Refetch when any SERVER filter changes, or when permission becomes ready
    // (allowedIndustries changes the effective empty-selection scope).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, permissionReady, industryValues.join('|'), useCaseValues.join('|'), subTopicIdValues.join('|'), assetIdValues.join('|'), allowedIndustries.join('|')]);

  // Client-side pagination over the (fully-loaded, filtered) rows — the aligned
  // page-number UI. ListMotions returns the whole curated set in one call, so
  // unlike the Pipeline tab (server keyset) this is plain useCollection paging.
  const pageSize = preferences.pageSize ?? 20;
  // ListMotions returns the whole set in one call, so sorting is over the FULL
  // dataset (not a page) — safe + accurate. useCollection sorts THEN paginates,
  // and spreads sortingColumn/onSortingChange onto the Table via collectionProps.
  const { items: pageItems, paginationProps, collectionProps } = useCollection(filteredMotions, {
    pagination: { pageSize },
    sorting: {},
  });

  const handlePreferencesChange = (detail: CollectionPreferencesProps.Preferences) => {
    setPreferences(detail);
    savePreferences(detail);
  };

  const handleColumnWidthsChange = (widths: readonly number[]) => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      visibleColumns.forEach((col, i) => {
        if (col.id && typeof widths[i] === 'number') next[col.id] = widths[i];
      });
      saveColumnWidths(next);
      return next;
    });
  };

  // Batch delete: N parallel DeleteMotion calls (no batch op). Refresh + clear
  // selection on success; surface a partial-failure count if any call fails.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    const ids = selectedItems.map((m) => m.motionId).filter((v): v is string => !!v);
    if (ids.length === 0) return;
    setDeleting(true);
    setDeleteError(null);
    const results = await Promise.allSettled(
      ids.map((motionId) => getIvtClient().send(new DeleteMotionCommand({ motionId }))),
    );
    // Keep the rejection REASONS, not just the count: DeleteMotion now requires the
    // write role, and "请重试" is wrong advice for a 403 — the retry can only fail
    // again until the user's LDAP groups change.
    const rejected = results.filter(
      (r): r is PromiseRejectedResult => r.status === 'rejected',
    );
    setDeleting(false);
    setConfirmingDelete(false);
    setSelectedItems([]);
    if (rejected.length > 0) {
      const denied = rejected.filter((r) => isDenied(r.reason));
      // Every denial in one batch has the same cause (same caller, same route), so the
      // first hint speaks for all of them.
      const hint = denialHint(denied[0]?.reason);
      const head = `${rejected.length}/${ids.length} 个 Motion 删除失败`;
      if (hint && denied.length === rejected.length) {
        setDeleteError(`${head}：${hint}`);
      } else if (hint) {
        // Mixed batch: naming both counts keeps the retry advice attached only to the
        // failures it applies to.
        setDeleteError(
          `${head}。其中 ${denied.length} 个因权限被拒：${hint} 其余 ${rejected.length - denied.length} 个失败原因未知，可重试。`,
        );
      } else {
        setDeleteError(`${head}，请重试。`);
      }
    }
    setReloadKey((k) => k + 1); // refetch the list either way
  };

  // Visible columns = row header (always) + chosen hideable set; minWidth floor +
  // any persisted drag width applied. (Same idiom as the Pipeline tab.)
  const chosenHideable =
    preferences.visibleContent && preferences.visibleContent.length > 0
      ? preferences.visibleContent
      : HIDEABLE_COLUMN_IDS;
  const visibleColumnIds = new Set<string>([ROW_HEADER_ID, ...chosenHideable]);

  const allColumns: TableProps.ColumnDefinition<MotionListItem>[] = [
    {
      id: 'name',
      header: COLUMN_LABELS.name,
      // Name links to the Motion detail page /ivt/motions/{id} (route registered
      // by Wenyuan's D4). Carry the origin tab so the detail breadcrumb returns
      // here to the Motion List tab. Plain text when a row somehow lacks an id.
      cell: (m) =>
        m.motionId ? (
          <Link onFollow={() => navigate(`/ivt/motions/${m.motionId}`, { state: { fromTab: 'motions' } })}>
            {m.name ?? m.motionId}
          </Link>
        ) : (
          (m.name ?? '—')
        ),
      isRowHeader: true,
      // Sort by display name (case-insensitive, locale-aware). sortingField gives
      // the sort indicator a STABLE identity so it survives the per-render rebuild
      // of visibleColumns (reference-only matching would drop the arrow).
      sortingField: 'name',
      sortingComparator: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
    },
    // Pipeline Status = total + tagged in one cell ("Total 10, Tagged 2"). Sorts
    // on the total count (the leading figure). Empty pipeline → "Total 0, Tagged 0".
    {
      id: 'pipelineStatus',
      header: COLUMN_LABELS.pipelineStatus,
      cell: (m) => `Total ${m.pipelineTotal ?? 0}, Tagged ${m.pipelineTagged ?? 0}`,
      sortingField: 'pipelineTotal',
      sortingComparator: (a, b) => (a.pipelineTotal ?? 0) - (b.pipelineTotal ?? 0),
    },
    {
      id: 'createdAt',
      header: COLUMN_LABELS.createdAt,
      cell: (m) => formatDate(m.createdAt),
      // Sort chronologically on the raw ISO timestamp (not the formatted string).
      // createdAt is optional → a missing/invalid date parses to NaN; coerce to 0
      // (sorts first ascending) so the comparator never returns NaN (undefined sort).
      sortingField: 'createdAt',
      sortingComparator: (a, b) => {
        const ta = Date.parse(a.createdAt ?? '');
        const tb = Date.parse(b.createdAt ?? '');
        return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
      },
    },
    {
      id: 'totalArr',
      header: COLUMN_LABELS.totalArr,
      cell: (m) => formatArr(m.totalArr),
      sortingField: 'totalArr',
      sortingComparator: (a, b) => (a.totalArr ?? 0) - (b.totalArr ?? 0),
    },
  ];

  const visibleColumns: TableProps.ColumnDefinition<MotionListItem>[] = allColumns
    .filter((c) => visibleColumnIds.has(c.id as string))
    .map((c) => {
      const id = c.id as (typeof COLUMN_IDS)[number];
      return { ...c, minWidth: COLUMN_MIN_WIDTH[id], ...(columnWidths[id] ? { width: columnWidths[id] } : {}) };
    });

  return (
    <div className="ivt-motion-page">
      {/* Title row — self-drawn (NOT the shared PageHeader, whose white bar / full-
          width divider don't match this page). Sits INSIDE the page's 32px padding
          so the right-side actions align with the table's right edge below. */}
      <div className="ivt-motion-page__header">
        <Box variant="h1" fontSize="heading-l" fontWeight="bold">
          Sales Motion
        </Box>
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <Button variant="normal" iconName="add-plus" onClick={() => setCreating(true)}>
            Create New Motion
          </Button>
          {/* Motion / Pipeline view switch — a segmented control on the header row
              (replaces the old left sub-nav). Mirrored in ?tab= so it survives a
              round-trip to the Motion detail page. */}
          <SegmentedControl
            selectedId={subView}
            onChange={({ detail }) => setSubView(detail.selectedId as SubView)}
            label="Motion 视图切换"
            options={[
              { id: 'motions', text: 'Motion View' },
              { id: 'pipeline', text: 'Pipeline View' },
            ]}
          />
        </SpaceBetween>
      </div>

      {creating && (
        <MotionCreateModal
          allowedIndustries={allowedIndustries}
          onClose={() => setCreating(false)}
          onCreated={(motionId) => {
            // pages-design §2③: CreateMotion → navigate to the Motion detail page
            // (Wenyuan's D4, /ivt/motions/{motionId}, route registered). Carry the
            // origin tab so the detail breadcrumb returns to the Motion List tab.
            setCreating(false);
            if (motionId) navigate(`/ivt/motions/${motionId}`, { state: { fromTab: 'motions' } });
            else setReloadKey((k) => k + 1); // defensive: no id → just refresh the list
          }}
        />
      )}

      {confirmingDelete && (
        <MotionDeleteModal
          motions={selectedItems}
          deleting={deleting}
          onConfirm={doDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}

      {/* fail-CLOSED: permission fetch failed → do NOT show the list (which would
          require falling open to all industries). Show a permission error; no
          retry button (refresh the page to retry — per product). */}
      {permissionError ? (
        <Alert type="error" header="无法获取访问权限">
          <div>
            未能获取你可访问的行业，暂时无法加载 Motion 列表。
          </div>
          <div style={{ marginTop: 8 }}>请刷新页面重试；若持续失败请联系管理员。</div>
        </Alert>
      ) : (
      <div className="ivt-motion-page__content">
          {subView === 'pipeline' ? (
            <MotionPipelineList allowedIndustries={allowedIndustries} permissionReady={permissionReady} />
          ) : (
          <>
          {/* Shared filter row (Figma): Industry / Use Case / Motion / Topic /
              Asset. Industry/UC/Topic/Asset are server-side (refetch);
              Motion is local (filters the loaded rows by chosen Motions). */}
          <MotionFilters
            industries={industries}
            onIndustriesChange={setIndustries}
            allowedIndustries={allowedIndustries}
            permissionReady={permissionReady}
            useCases={useCases}
            onUseCasesChange={setUseCases}
            motionOptions={motionNameOptions}
            motions={selectedMotions}
            onMotionsChange={setSelectedMotions}
            subTopics={subTopics}
            onSubTopicsChange={setSubTopics}
            assets={assets}
            onAssetsChange={setAssets}
          />

          {deleteError && (
            <Alert type="error" dismissible onDismiss={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}

          <div className="ivt-motion-card">
            <Table<MotionListItem>
              {...collectionProps}
              variant="embedded"
              loading={loading}
              loadingText="加载 Motions…"
              // Multi-select → the header "Delete Motion" action deletes the checked rows.
              selectionType="multi"
              selectedItems={selectedItems}
              onSelectionChange={({ detail }) => setSelectedItems([...detail.selectedItems])}
              ariaLabels={{
                selectionGroupLabel: 'Motion 选择',
                allItemsSelectionLabel: () => '全选',
                itemSelectionLabel: (_data, row) => `选择 ${row.name ?? row.motionId}`,
              }}
              header={
                <Header
                  variant="h2"
                  counter={selectedItems.length ? `(${selectedItems.length}/${filteredMotions.length})` : `(${filteredMotions.length})`}
                  actions={
                    <Button
                      disabled={selectedItems.length === 0}
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Delete Motion
                    </Button>
                  }
                >
                  Motion list
                </Header>
              }
              items={pageItems}
              trackBy="motionId"
              resizableColumns
              onColumnWidthsChange={({ detail }) => handleColumnWidthsChange(detail.widths)}
              pagination={<Pagination {...paginationProps} />}
              preferences={
                <CollectionPreferences
                  title="偏好设置"
                  confirmLabel="确定"
                  cancelLabel="取消"
                  preferences={preferences}
                  onConfirm={({ detail }) => handlePreferencesChange(detail)}
                  pageSizePreference={{ title: '每页条数', options: PAGE_SIZE_OPTIONS }}
                  visibleContentPreference={{ title: '显示列', options: VISIBLE_CONTENT_OPTIONS }}
                />
              }
              columnDefinitions={visibleColumns}
              empty={
                error ? (
                  <Alert
                    type="error"
                    header="无法加载 Motions"
                    action={<Button onClick={() => setReloadKey((k) => k + 1)}>重试</Button>}
                  >
                    {error}
                  </Alert>
                ) : permissionReady && allowedIndustries.length === 0 ? (
                  // Zero-permission user: the list is deliberately short-circuited to
                  // empty (not fetched) because the caller has no industry permission.
                  // Say so explicitly — otherwise this falls through to "No motions
                  // yet / create your first", which misleads (it's a permission gap,
                  // not an empty workspace). Mirrors the Impact page's 无 Industry 权限
                  // banner.
                  <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                    <SpaceBetween size="xs">
                      <b>无 Industry 权限</b>
                      <span>当前账号没有任何 Industry 的查看权限，暂无 Motion 可展示。如需权限请联系管理员。</span>
                    </SpaceBetween>
                  </Box>
                ) : hasActiveFilter ? (
                  // Some filter is active (name search or a server filter) but
                  // nothing matched — distinct from the "no motions yet" state.
                  // Note: results are also industry-scoped server-side, so a
                  // partially-permitted user may have matching Opp in industries
                  // they can't see — flagged neutrally below.
                  <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                    <SpaceBetween size="xs">
                      <b>No matches</b>
                      <span>没有匹配当前筛选条件的 Motion。</span>
                      <Box variant="small" color="text-body-secondary">部分行业的数据可能因权限未展示。</Box>
                      <Button onClick={clearAllFilters}>清除筛选</Button>
                    </SpaceBetween>
                  </Box>
                ) : (
                  <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                    <SpaceBetween size="xs">
                      <b>No motions yet</b>
                      <span>Create your first Sales Motion to start curating pipeline.</span>
                      <Button onClick={() => setCreating(true)}>Create New Motion</Button>
                    </SpaceBetween>
                  </Box>
                )
              }
            />
          </div>
          </>
          )}
      </div>
      )}
    </div>
  );
}
